// Sign in with Google: Google Identity Services draws its own button, and hands back an
// ID token that the server checks (src/helpers/googleSignIn.ts on the server).
//
// The button only shows when the server has a client id (GOOGLE_CLIENT_ID), which it
// tells the page at /v1/auth/google/config, so turning it on needs no rebuild.

import api from './Api';
import * as JKYModel from '../custom-types';

import { MFA_REQUIRED } from './TwoStep';

export const GSI_SRC = 'https://accounts.google.com/gsi/client';

/** What the server sends back when someone new to Jookable picks their Google account. */
export interface PendingGoogleSignup {
  pendingToken: string;
  suggestedUsername: string;
  email: string;
}

/**
 * Either they are signed in, they are new and pick a username next, or their account has
 * two-step sign-in on and they type the code from their app next.
 */
export type GoogleSignInResult =
  | { kind: 'signed-in'; user: JKYModel.User; token: string }
  | { kind: 'pending'; pending: PendingGoogleSignup }
  | { kind: 'mfa'; mfaToken: string };

let clientIdRequest: Promise<string | null> | null = null;
let scriptRequest: Promise<any> | null = null;

/** The Google client id, or null when Google sign-in is off. Asked once per page load. */
export const loadGoogleClientId = (): Promise<string | null> => {
  if (!clientIdRequest) {
    clientIdRequest = api
      .auth('google/config', 'GET', null)
      .then((response: any) => (response && typeof response.clientId === 'string' && response.clientId) || null)
      .catch(() => {
        // Asked again next time, rather than remembering a blip as "off".
        clientIdRequest = null;
        return null;
      });
  }
  return clientIdRequest;
};

/** Loads Google's script once and resolves with window.google.accounts.id. */
export const loadGoogleScript = (): Promise<any> => {
  const ready = () => (window as any).google && (window as any).google.accounts && (window as any).google.accounts.id;
  if (ready()) {
    return Promise.resolve(ready());
  }
  if (!scriptRequest) {
    scriptRequest = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => (ready() ? resolve(ready()) : reject(new Error('Google sign-in did not start')));
      script.onerror = () => {
        scriptRequest = null;
        script.remove();
        reject(new Error('Google sign-in could not load'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptRequest;
};

/** Sends Google's ID token to the server. */
export const signInWithGoogleCredential = async (credential: string): Promise<GoogleSignInResult> => {
  let response: any;
  try {
    response = await api.auth('google', 'POST', { credential });
  } catch (error) {
    if (error && error.code === MFA_REQUIRED && error.mfaToken) {
      return { kind: 'mfa', mfaToken: error.mfaToken };
    }
    throw error;
  }
  if (response && response.user && response.token) {
    return { kind: 'signed-in', user: response.user, token: response.token };
  }
  if (response && response.pendingToken) {
    return {
      kind: 'pending',
      pending: {
        pendingToken: response.pendingToken,
        suggestedUsername: response.suggestedUsername || '',
        email: response.email || '',
      },
    };
  }
  throw new Error('Google sign-in did not work. Please try again.');
};

/** Makes the account for someone new, once they have picked a username. */
export const completeGoogleSignup = async (
  pendingToken: string,
  username: string,
  acceptedTerms: boolean,
): Promise<{ user: JKYModel.User; token: string }> => {
  const response = await api.auth('google/complete', 'POST', { pendingToken, username, acceptedTerms });
  if (!response || !response.user || !response.token) {
    throw new Error('Your account could not be created. Please try again.');
  }
  return { user: response.user, token: response.token };
};

/** The same rule as the server: 4 to 20 characters. Null when it is fine. */
export const usernameLengthProblem = (username: string): string | null => {
  const name = username.trim();
  if (!name) return 'Please pick a username.';
  if (name.length < 4 || name.length > 20) return 'Your username must be 4 to 20 characters.';
  return null;
};

/** For tests: forget the cached client id and script. */
export const resetGoogleSignInForTests = () => {
  clientIdRequest = null;
  scriptRequest = null;
};
